<script>
    import { onMount, onDestroy } from 'svelte';
    import { navigationStore } from '../../../stores/navigationStore';
    import { documentStructureService } from '../../../services/documentStructureService';
    import NavigationTree from './NavigationTree.svelte';
    import DraggableResizablePanel from '../../shared/DraggableResizablePanel.svelte';

    export let editor;

    let searchInput;
    
    let isOpen = false;
    let structure = [];
    let filter = '';

    // Subscribe to store
    const unsubscribe = navigationStore.subscribe(state => {
        console.log('[NavigationPane] Store update received:', { isOpen: state.isOpen });
        isOpen = state.isOpen;
        structure = state.structure;
        filter = state.filter;
    });


    // Handle filter change
    function handleFilterChange(e) {
        navigationStore.updateFilter(e.target.value);
    }

    // Handle keyboard shortcuts
    function handleKeydown(e) {
        // Close on Escape
        if (e.key === 'Escape' && isOpen) {
            navigationStore.close();
            editor?.view?.focus();
        }
        
        // Focus search on Ctrl/Cmd+F when pane is open
        if ((e.ctrlKey || e.metaKey) && e.key === 'f' && isOpen) {
            e.preventDefault();
            searchInput?.focus();
        }
    }

    // Get filtered structure
    $: filteredStructure = filter 
        ? documentStructureService.filterStructure(structure, filter)
        : structure;

    onMount(() => {
        document.addEventListener('keydown', handleKeydown);
    });

    onDestroy(() => {
        unsubscribe();
        document.removeEventListener('keydown', handleKeydown);
    });
</script>

<DraggableResizablePanel
    show={isOpen}
    title="Spec Outline"
    storageKey="navigation-pane"
    theme="navigation"
    initialPosition={{ x: 20, y: 100 }}
    initialWidth={280}
    initialHeight={500}
    minWidth={250}
    minHeight={200}
    maxWidth={600}
    maxHeight={window.innerHeight - 40}
    draggable={true}
    resizable={true}
    on:close={() => {
        navigationStore.close();
        editor?.view?.focus();
    }}
>

    <div class="navigation-pane-content">
        <!-- Search -->
        <div class="navigation-search">
            <input
                type="text"
                placeholder="Filter headings..."
                value={filter}
                on:input={handleFilterChange}
                bind:this={searchInput}
                class="search-input"
            />
        </div>

        <!-- Content -->
        <div class="navigation-content">
            {#if filteredStructure.length > 0}
                <NavigationTree items={filteredStructure} {editor} />
            {:else if filter}
                <div class="empty-state">
                    No headings match "{filter}"
                </div>
            {:else}
                <div class="empty-state">
                    No headings found in document
                </div>
            {/if}
        </div>
</DraggableResizablePanel>

<style>
    /* Content wrapper */
    .navigation-pane-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    
    /* Override panel styling for navigation theme */
    :global(.draggable-panel--navigation) {
        font-family: var(--font-family-base);
    }
    
    :global(.draggable-panel--navigation) :global(.draggable-panel-content) {
        padding: 0;
    }

    .navigation-search {
        padding: var(--spacing-3) var(--spacing-4);
        border-bottom: 1px solid var(--border-color-default);
    }

    .search-input {
        width: 100%;
        padding: var(--spacing-2) var(--spacing-3);
        font-size: var(--font-size-sm);
        color: var(--color-text-primary);
        background: var(--color-bg-input);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-sm);
        transition: border-color var(--transition-colors);
    }

    .search-input:focus {
        border-color: var(--color-spec-figure-text);
        outline: none;
    }

    .search-input::placeholder {
        color: var(--color-text-tertiary);
    }

    .navigation-content {
        flex: 1;
        padding: var(--spacing-2) 0;
        overflow: hidden auto;
    }

    .empty-state {
        padding: var(--spacing-6) var(--spacing-4);
        font-size: var(--font-size-sm);
        text-align: center;
        color: var(--color-text-tertiary);
    }


    /* Scrollbar styling - using spec colors */
    .navigation-content::-webkit-scrollbar {
        width: 6px;
    }

    .navigation-content::-webkit-scrollbar-track {
        background: var(--color-surface-header-spec);
    }

    .navigation-content::-webkit-scrollbar-thumb {
        background: var(--color-spec-figure-text);
        border-radius: var(--border-radius-sm);
        opacity: 0.5;
    }

    .navigation-content::-webkit-scrollbar-thumb:hover {
        opacity: 0.7;
    }
</style>